import type { CameraScanPhase, CameraStreamErrorKind } from '@dutchie/capacitor-hardware';

/**
 * Everything the operator reads while scanning on a phone, kept local to Mobile Checkout.
 *
 * Deliberately not in `@dutchie-pos/requests`: that package carries the wire contract, and copy is
 * not a contract. Register keeps its own wording under `CameraIdScan/camera-copy.ts`, and the two
 * genuinely differ — a phone is held in one hand and can be rotated, which is why the advisory
 * below exists here and has no counterpart on the counter. Sharing these strings would make every
 * wording change a shared-package change with two consumers to re-verify.
 *
 * The phase tokens come from the hardware package, which owns the scan lifecycle; only their
 * presentation lives here.
 */

const CAMERA_MESSAGES: Record<CameraStreamErrorKind, string> = {
  insecure: 'The camera needs a secure (https) connection. Upload a photo of the barcode instead.',
  denied: 'Camera access is blocked. Allow it in your browser settings, then try again.',
  notFound: 'No camera was found on this device. Upload a photo of the barcode instead.',
  busy: 'The camera is already in use by another app.',
  unknown: 'The camera could not be started.',
};

/** Why the camera would not open, as the operator needs to hear it. */
export const cameraErrorMessage = (kind: CameraStreamErrorKind): string => CAMERA_MESSAGES[kind];

/** Said before the scan: this resolution cannot reach the width a dense PDF417 needs, however well framed. */
export const CAMERA_LOW_RESOLUTION_MESSAGE =
  "This camera's resolution is too low to read an ID barcode reliably. Upload image works better here.";

/** A portrait stream is narrower than the reliable width however the card is held, and CSS adds no pixels. */
export const CAMERA_ROTATE_MESSAGE =
  'Turn the phone sideways to scan — upright, the camera is too narrow to read the barcode.';

/** Names the barcode, not the card: the guide is a landscape band, and a whole card inside it reads too small. */
export const ID_SCAN_GUIDE_HINT = 'Scan the barcode on the back.';

/** `searching` is the only tone that means "frames are going in and nothing is coming out". */
export type ScanGuideTone = 'neutral' | 'searching' | 'found' | 'error';

export type ScanGuideStatus = { tone: ScanGuideTone; label: string };

/** Neutral before a frame exists: saying there is no barcode while the camera starts is what makes a working scanner look broken. */
const NEUTRAL_STATUS: ScanGuideStatus = { tone: 'neutral', label: 'Starting camera' };
const SEARCHING_STATUS: ScanGuideStatus = { tone: 'searching', label: 'Looking for barcode' };
const STOPPED_STATUS: ScanGuideStatus = { tone: 'error', label: 'The camera stopped' };

/**
 * `satisfies Record<CameraScanPhase, ...>` is the point: a phase added in the hardware package
 * fails here at compile time rather than silently mapping to nothing on screen.
 */
const SCAN_STATUSES = {
  starting: NEUTRAL_STATUS,
  'waiting-for-frames': NEUTRAL_STATUS,
  aborted: NEUTRAL_STATUS,
  decoding: SEARCHING_STATUS,
  // An expired window was searching right up to the end; the caller then swaps in its failure copy.
  'timed-out': SEARCHING_STATUS,
  decoded: { tone: 'found', label: 'Barcode read' },
  'no-decoder': { tone: 'error', label: 'This device cannot scan barcodes' },
  'no-stream': STOPPED_STATUS,
  'stream-ended': STOPPED_STATUS,
} satisfies Record<CameraScanPhase, ScanGuideStatus>;

/** Maps the scanner's phases, so the guide cannot claim a state the decoder is not in. */
export const scanStatus = (phase: CameraScanPhase | null): ScanGuideStatus =>
  phase ? SCAN_STATUSES[phase] : NEUTRAL_STATUS;
